import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const brands = "Christian Louboutin \u2022 Sephora \u2022 Bvlgari \u2022 Belvedere \u2022 Gaurav Gupta \u2022 Vogue \u2022 YPO \u2022 Nykaaland \u2022 Adidas \u2022 Manyavar \u2022 Indian Express \u2022 Sony \u2022 The FaceShop \u2022 Benefit Cosmetics \u2022 Foxtale";

export default function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="about"
      data-testid="about-section"
      ref={ref}
      className="bg-white py-20 md:py-36 px-6 md:px-16 lg:px-24"
    >
      <div className="max-w-[1100px] mx-auto">
        <div className="flex flex-col md:flex-row gap-10 md:gap-20 items-start">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9 }}
            className="w-full md:w-2/5"
          >
            <img
              src="/portfolio/drive_palak.jpg"
              alt="Palak, CinCin Events Founder"
              className="w-full h-[55vh] md:h-[68vh] object-cover"
              data-testid="about-palak-image"
            />
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="w-full md:w-3/5 max-w-[620px]"
          >
            <span className="label-text" data-testid="about-label">The Mind Behind CinCin</span>
            <div className="editorial-line mt-4 mb-8" />

            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A0A0A] leading-[1.1]">
              Palak
            </h2>

            {/* Intro hook - slightly larger, darker */}
            <p
              className="mt-10 text-[15px] leading-[1.9] text-[#0A0A0A]/80 font-light"
              style={{ letterSpacing: '0.3px' }}
            >
              Palak approaches every celebration as a canvas, not a checklist.
            </p>

            {/* Bio blocks - airy, rhythmic spacing */}
            <div className="mt-7 space-y-6">
              <p
                className="text-[13px] leading-[1.9] text-[#0A0A0A]/55 font-light"
                style={{ letterSpacing: '0.3px' }}
              >
                Palak studied Event Management in Sheffield, UK, and began her career in Dubai with Qode Luxury PR & Events, working on global brands such as Christian Louboutin, Sephora, COS, and Jaeger-LeCoultre.
              </p>

              <p
                className="text-[13px] leading-[1.9] text-[#0A0A0A]/55 font-light"
                style={{ letterSpacing: '0.3px' }}
              >
                She then moved to Mumbai, where she worked with Maganimous on large-scale experiences for Vogue, YPO, and leading beauty and lifestyle brands, before building the events vertical at Schbang.
              </p>

              <p
                className="text-[13px] leading-[1.9] text-[#0A0A0A]/55 font-light"
                style={{ letterSpacing: '0.3px' }}
              >
                Her work also includes high-profile weddings with Shaadi Squad, including several A-list celebrities.
              </p>

              <p
                className="text-[13px] leading-[1.9] text-[#0A0A0A]/55 font-light"
                style={{ letterSpacing: '0.3px' }}
              >
                With over a decade of hands-on experience across luxury, corporate, and wedding ecosystems, she founded CinCin Events, a bespoke luxury event and wedding decor company focused on elevated, design-led celebrations.
              </p>
            </div>

            {/* Signature line - editorial whisper */}
            <p
              className="font-serif text-[13px] italic leading-[1.9] text-[#0A0A0A]/35 mt-10"
              style={{ letterSpacing: '0.2px' }}
            >
              Known for her sharp eye and instinct for detail, Palak believes it is the smallest elements that create the most unforgettable experiences.
            </p>

            <p
              className="font-light text-[13px] leading-[1.9] text-[#0A0A0A]/50 mt-6"
              style={{ letterSpacing: '0.3px' }}
            >
              Her work is not just about how it looks, but how it feels, long after.
            </p>

            {/* Brand credibility strip */}
            <div className="mt-14 pt-8 border-t border-[#0A0A0A]/6">
              <p className="text-[9px] tracking-[0.3em] uppercase text-[#8C8C8C]/50 font-light mb-4">
                Worked With
              </p>
              <p className="text-[9px] tracking-[0.15em] text-[#0A0A0A]/30 font-light mb-3" style={{ letterSpacing: '0.2px' }}>
                Over a decade of experience across India, Dubai, and the UK
              </p>
              <p
                data-testid="palak-brand-strip"
                className="text-[10px] tracking-[0.12em] leading-[2.2] text-[#0A0A0A]/25 font-light"
              >
                {brands}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
